import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';
import { getMapConfig, MapVariableConfig } from '../../config/mapConfigs';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { getCountyFromGeoid } from '../../utils/countyFipsMapping';
import MapLegend from './MapLegend';
import MapVariableSelector from './MapVariableSelector';
import DataLoadingScopeSelector from './DataLoadingScopeSelector';

interface GenericMapProps {
  location: string;
  onDataLoaded?: (metrics: {
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
    minValue?: number;
    maxValue?: number;
  }) => void;
  hideVariableSelector?: boolean;
  lockedVariable?: string; // Lock to a specific variable (e.g., 'ev_adoption', 'total_chargers')
  allowedVariables?: string[]; // Optional subset of map variables to expose in the selector
  enableScopeSelector?: boolean; // Show data scope (county/region/California) selector
}

interface TractData {
  geofips: string;
  county: string;
  location_name: string;
  [key: string]: any; // Allow for different data fields
}

type MapMetrics = {
  currentValue: number;
  californiaAverage: number;
  year: number;
  variable: string;
  minValue?: number;
  maxValue?: number;
};

type CachedMapData = {
  tractData: TractData[];
  mapCenter: [number, number];
  mapZoom: number;
  metrics: MapMetrics | null;
};

const MAP_CACHE_MAX = 5;
const mapDataCache = new Map<string, CachedMapData>();

function mapCacheSet(key: string, value: CachedMapData) {
  if (mapDataCache.size >= MAP_CACHE_MAX) {
    const oldest = mapDataCache.keys().next().value;
    if (oldest !== undefined) mapDataCache.delete(oldest);
  }
  mapDataCache.set(key, value);
}

const GenericMapTiered: React.FC<GenericMapProps> = ({
  location,
  onDataLoaded,
  hideVariableSelector = false,
  lockedVariable,
  allowedVariables,
  enableScopeSelector = false
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariable, setSelectedVariable] = useState<string>(() => {
    // Prefer a locked variable if provided
    if (lockedVariable) return lockedVariable;
    // Otherwise, if a constrained set of variables is provided, default to the first one
    if (allowedVariables && allowedVariables.length > 0) {
      return allowedVariables[0];
    }
    // Fallback to carbon_footprint for legacy usages
    return 'carbon_footprint';
  });
  const [tractData, setTractData] = useState<TractData[]>([]);
  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [loadingStage, setLoadingStage] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [mapCenter, setMapCenter] = useState<[number, number]>([36.7783, -119.4179]); // Default California center
  const [mapZoom, setMapZoom] = useState<number>(6); // Default California zoom
  const [mapReady, setMapReady] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false); // Prevent multiple simultaneous loads
  const lastLoadKeyRef = useRef<string>(''); // Avoid duplicate loads for same inputs
  const isMountedRef = useRef(true);
  const [dataScope, setDataScope] = useState<'county' | 'region' | 'california'>('county');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Update selectedVariable when lockedVariable changes
  useEffect(() => {
    if (lockedVariable) {
      setSelectedVariable(lockedVariable);
    }
  }, [lockedVariable]);

  // When a set of allowed variables is provided (e.g., on People/Transportation pages),
  // make sure the selected variable is one of them, defaulting to the first allowed option.
  useEffect(() => {
    if (!lockedVariable && allowedVariables && allowedVariables.length > 0) {
      setSelectedVariable((prev) =>
        allowedVariables.includes(prev) ? prev : allowedVariables[0]
      );
    }
  }, [allowedVariables, lockedVariable]);

  // Get current map configuration
  const currentConfig = useMemo(() => getMapConfig(selectedVariable), [selectedVariable]);

  // Function to get map center based on county
  const getMapCenter = (county: string): [number, number] => {
    const countyCenters: Record<string, [number, number]> = {
      'Alameda County': [37.7749, -122.2711], // Oakland area
      'Alpine County': [38.5974, -119.8193], // Alpine
      'Amador County': [38.4467, -120.6511], // Jackson
      'Butte County': [39.6679, -121.6008], // Chico
      'Calaveras County': [38.2041, -120.5547], // San Andreas
      'Colusa County': [39.2143, -122.0094], // Colusa
      'Contra Costa County': [37.9199, -122.3482], // Martinez
      'Del Norte County': [41.7558, -124.2016], // Crescent City
      'El Dorado County': [38.7786, -120.5247], // Placerville
      'Fresno County': [36.7378, -119.7871], // Fresno
      'Glenn County': [39.5985, -122.3920], // Willows
      'Humboldt County': [40.8665, -124.0828], // Eureka
      'Imperial County': [32.8475, -115.5694], // El Centro
      'Inyo County': [36.5110, -117.3104], // Independence
      'Kern County': [35.3433, -118.7272], // Bakersfield
      'Kings County': [36.0753, -119.8157], // Hanford
      'Lake County': [39.0586, -122.9155], // Lakeport
      'Lassen County': [40.4174, -120.6506], // Susanville
      'Los Angeles County': [34.0522, -118.2437], // LA
      'Madera County': [37.2181, -119.7619], // Madera
      'Marin County': [38.0834, -122.7633], // San Rafael
      'Mariposa County': [37.4849, -119.9663], // Mariposa
      'Mendocino County': [39.3077, -123.7995], // Ukiah
      'Merced County': [37.3022, -120.4829], // Merced
      'Modoc County': [41.5898, -120.7250], // Alturas
      'Mono County': [37.9389, -118.8870], // Bridgeport
      'Monterey County': [36.6002, -121.8947], // Salinas
      'Napa County': [38.2975, -122.2869], // Napa
      'Nevada County': [39.2619, -121.0161], // Nevada City
      'Orange County': [33.7175, -117.8311], // Orange County
      'Placer County': [39.0000, -120.7500], // Auburn
      'Plumas County': [40.0037, -120.8397], // Quincy
      'Riverside County': [33.9533, -117.3962], // Riverside
      'Sacramento County': [38.5816, -121.4944], // Sacramento
      'San Benito County': [36.6061, -121.0756], // Hollister
      'San Bernardino County': [34.1083, -117.2898], // San Bernardino
      'San Diego County': [32.7157, -117.1611], // San Diego
      'San Francisco County': [37.7749, -122.4194], // SF
      'San Joaquin County': [37.7799, -121.9780], // Stockton
      'San Luis Obispo County': [35.2828, -120.6596], // San Luis Obispo
      'San Mateo County': [37.4969, -122.3331], // Redwood City
      'Santa Barbara County': [34.4221, -119.7027], // Santa Barbara
      'Santa Clara County': [37.3541, -121.9552], // San Jose
      'Santa Cruz County': [37.0351, -122.0096], // Santa Cruz
      'Shasta County': [40.5865, -122.3917], // Redding
      'Sierra County': [39.5800, -120.5169], // Downieville
      'Siskiyou County': [41.5889, -122.5406], // Yreka
      'Solano County': [38.2784, -122.0093], // Fairfield
      'Sonoma County': [38.5270, -122.9295], // Santa Rosa
      'Stanislaus County': [37.5591, -120.9969], // Modesto
      'Sutter County': [39.0347, -121.7536], // Yuba City
      'Tehama County': [40.1255, -122.2347], // Red Bluff
      'Trinity County': [40.6507, -123.1126], // Weaverville
      'Tulare County': [36.2077, -119.3473], // Visalia
      'Tuolumne County': [37.9411, -120.2319], // Sonora
      'Ventura County': [34.3705, -119.1391], // Ventura
      'Yolo County': [38.6845, -121.7732], // Davis area
      'Yuba County': [39.1404, -121.6169], // Marysville
    };
    
    return countyCenters[county] || [36.7783, -119.4179]; // Default to California center
  };

  // Function to get region information for a location
  const getLocationRegionInfo = async (locationName: string): Promise<{county: string, region: number, regionName: string, geofips: number} | null> => {
    try {
      const { data, error } = await supabaseClient
        .from('regions')
        .select('geofips, county, region, region_name')
        .ilike('location_name', locationName) // Use ilike for case-insensitive search
        .eq('geography_type', 'City') // Use 'City' with capital C as shown in the data
        .limit(1);
      
      if (error) {
        console.error('Error fetching region info:', error);
        throw error;
      }
      if (!data || data.length === 0) {
        return null;
      }
      
      return {
        geofips: data[0].geofips,
        county: data[0].county,
        region: data[0].region,
        regionName: data[0].region_name
      };
    } catch (err) {
      console.error('Error fetching region info:', err);
      return null;
    }
  };

  // Resolve data field for the selected variable
  const getDataFieldForVariable = useCallback((variableId?: string): string => {
    if (variableId === 'income') return 'b19013_001e';
    if (variableId === 'ev_adoption') return 'evs_per_100_people';
    if (variableId === 'total_chargers') return 'total_chargers';
    if (variableId === 'zero_vehicle_households') return 'pct_zero_vehicle_households';
    if (variableId === 'vehicles_per_household') return 'vehicles_per_household';
    if (variableId === 'population_density') return 'persons_per_sqmi';
    if (variableId === 'political_lean') return 'dem_minus_rep';
    if (variableId === 'carbon_footprint') return 'total_hh';
    if (variableId === 'commute_time') return 'avg_travel_time';
    if (variableId === 'owner_occupied_housing') return 'pct_owner_occupied';
    if (variableId === 'educational_attainment') return 'pct_bachelor_or_higher';
    return 'value'; // Default field name
  }, []);

  // Add global error handler for Leaflet errors (unmount/zoom race)
  useEffect(() => {
    const originalErrorHandler = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      const msg = typeof message === 'string' ? message : (error?.message ?? '');
      if (msg.includes('_leaflet_pos') || msg.includes("reading '_leaflet_pos'")) {
        return true;
      }
      if (originalErrorHandler) {
        return originalErrorHandler(message, source, lineno, colno, error);
      }
      return false;
    };

    return () => {
      window.onerror = originalErrorHandler;
    };
  }, []);

  // Load GeoJSON data
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch('/data/california_tracts_optimized.geojson');
        const data = await response.json();
        setGeoJsonData(data);
      } catch (err) {
        console.error('Error loading GeoJSON:', err);
        setError('Failed to load map data');
      }
    };

    loadGeoJSON();
  }, []);

  // Data loading function based on location and scope (county/region/California)
  const loadDataByLocation = useCallback(async () => {
    if (!currentConfig) {
      console.error('❌ No config found for variable:', selectedVariable);
      setError(`No configuration found for variable: ${selectedVariable}`);
      setLoading(false);
      setLoadingProgress(0);
      return;
    }

    const loadKey = `${selectedVariable}-${location}-${dataScope}`;
    const cached = mapDataCache.get(loadKey);
    if (cached) {
      lastLoadKeyRef.current = loadKey;
      setLoading(false);
      setError(null);
      setLoadingStage('');
      setLoadingProgress(100);
      setMapCenter(cached.mapCenter);
      setMapZoom(cached.mapZoom);
      setTractData(cached.tractData);
      setMapReady(false);
      if (cached.metrics && onDataLoaded) {
        onDataLoaded(cached.metrics);
      }
      return;
    }
    
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      return;
    }

    if (lastLoadKeyRef.current === loadKey) {
      return;
    }
    lastLoadKeyRef.current = loadKey;
    
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);
    setLoadingProgress(0);

    try {
      // Initialize location service if needed
      await supabaseLocationService.initialize();
      
      // Get location info to determine if it's a city, county, or state
      const locations = supabaseLocationService.getAvailableLocations();
      const locationInfo = locations.find(loc => loc.name === location);
      
      let allData: any[] = [];
      let nextMapCenter: [number, number] | null = null;
      let nextMapZoom: number | null = null;
      let targetCounty: string | null = null;
      let isState = false;
      let regionInfo: { county: string; region: number; regionName: string; geofips: number } | null = null;

      // Determine base geography for the selected location
      if (!locationInfo) {
        // Fallback: try to determine from location name
        if (location.toLowerCase() === 'california' || location.toLowerCase().includes('state')) {
          isState = true;
        } else if (location.toLowerCase().endsWith(' county')) {
          targetCounty = location.replace(/ County$/i, '');
        } else {
          // Assume it's a city and try to get county using the service method
          targetCounty = supabaseLocationService.getCountyForCity(location);
        }
      } else {
        // Use location info
        if (locationInfo.type === 'state') {
          isState = true;
        } else if (locationInfo.type === 'county') {
          targetCounty = locationInfo.name.replace(/ County$/i, '');
        } else if (locationInfo.type === 'city') {
          // For cities, show the county's tracts (since cities span multiple tracts)
          // Remove " County" suffix if present to normalize
          targetCounty = locationInfo.county.replace(/ County$/i, '');
        }
      }

      // Override geography based on selected data scope
      if (dataScope === 'california') {
        isState = true;
      } else if (dataScope === 'region') {
        try {
          // Region info is currently defined for City locations in the regions table
          regionInfo = await getLocationRegionInfo(location);
        } catch (regionErr) {
          // Fall back to county scope
        }
      }
      
      if (isState) {
        // Load all California data
        setLoadingStage('Loading California data...');
        setLoadingProgress(20);
        nextMapCenter = [36.7783, -119.4179]; // California center
        nextMapZoom = 6; // Zoom out to show all of California
        setMapCenter(nextMapCenter);
        setMapZoom(nextMapZoom);

        // Load all California data (pass empty array to get all data)
        try {
          setLoadingProgress(30);
          const californiaData = await Promise.race([
            loadRemainingCaliforniaData([], currentConfig),
            new Promise<any[]>((_, reject) => 
              setTimeout(() => reject(new Error('Data loading timeout after 30 seconds')), 30000)
            )
          ]);
          
          if (californiaData && californiaData.length > 0) {
            allData = [...californiaData];
            setTractData(allData);
            setLoadingProgress(90);
            const metrics = await calculateAndSetMetrics(allData, currentConfig);
            if (metrics && nextMapCenter && nextMapZoom) {
              mapCacheSet(loadKey, {
                tractData: allData,
                mapCenter: nextMapCenter,
                mapZoom: nextMapZoom,
                metrics
              });
            }
          } else {
            setError('No California data found');
          }
        } catch (loadError) {
          console.error('Error loading California data:', loadError);
          setError(`Failed to load California data: ${loadError instanceof Error ? loadError.message : 'Unknown error'}`);
        }
      } else if (regionInfo && dataScope === 'region') {
        // Load data for the full region (set of counties)
        setLoadingStage(`Loading ${regionInfo.regionName || 'regional'} data...`);
        setLoadingProgress(20);

        // Center map on the primary county for this region
        const regionCountyCenter = getMapCenter(`${regionInfo.county}`);
        nextMapCenter = regionCountyCenter;
        nextMapZoom = 7; // Slightly zoomed out to show multiple counties
        setMapCenter(nextMapCenter);
        setMapZoom(nextMapZoom);

        try {
          setLoadingProgress(30);
          const regionData = await Promise.race([
            loadDataForRegion(regionInfo.region, currentConfig),
            new Promise<any[]>((_, reject) =>
              setTimeout(() => reject(new Error('Region data loading timeout after 30 seconds')), 30000)
            )
          ]);

          let californiaDataForAverage: any[] | undefined = undefined;
          if (currentConfig.id === 'political_lean') {
            setLoadingProgress(50);
            try {
              californiaDataForAverage = await Promise.race([
                loadRemainingCaliforniaData([], currentConfig),
                new Promise<any[]>((_, reject) =>
                  setTimeout(() => reject(new Error('California data loading timeout')), 20000)
                )
              ]);
            } catch (calError) {
              // Fallback to default average
            }
          }

          if (regionData && regionData.length > 0) {
            allData = [...regionData];
            setTractData(allData);
            setLoadingProgress(90);
            const metrics = await calculateAndSetMetrics(allData, currentConfig, californiaDataForAverage);
            if (metrics && nextMapCenter && nextMapZoom) {
              mapCacheSet(loadKey, {
                tractData: allData,
                mapCenter: nextMapCenter,
                mapZoom: nextMapZoom,
                metrics
              });
            }
          } else {
            setError(`No data found for the selected region. Please check the browser console for details.`);
          }
        } catch (loadError) {
          console.error('Error loading region data:', loadError);
          setError(`Failed to load regional data: ${loadError instanceof Error ? loadError.message : 'Unknown error'}`);
        }
      } else if (targetCounty) {
        // Load county data
        // Normalize county name (remove " County" if present, then add it back for display)
        const normalizedCounty = targetCounty.replace(/ County$/i, '');
        setLoadingStage(`Loading ${normalizedCounty} County data...`);
        setLoadingProgress(20);

        // Set map center and zoom for the county
        const countyCenter = getMapCenter(`${normalizedCounty} County`);
        nextMapCenter = countyCenter;
        nextMapZoom = 9; // Zoom in to county level
        setMapCenter(nextMapCenter);
        setMapZoom(nextMapZoom);

        try {
          setLoadingProgress(30);
          const countyData = await Promise.race([
            loadDataForCounty(`${normalizedCounty} County`, currentConfig),
            new Promise<any[]>((_, reject) => 
              setTimeout(() => reject(new Error('Data loading timeout after 30 seconds')), 30000)
            )
          ]);
          
          // For political lean, also load all California data to calculate accurate California average
          let californiaDataForAverage: any[] | undefined = undefined;
          if (currentConfig.id === 'political_lean') {
            setLoadingProgress(50);
            try {
              californiaDataForAverage = await Promise.race([
                loadRemainingCaliforniaData([], currentConfig),
                new Promise<any[]>((_, reject) => 
                  setTimeout(() => reject(new Error('California data loading timeout')), 20000)
                )
              ]);
            } catch (calError) {
              // Fallback to default average
            }
          }
          
          if (countyData && countyData.length > 0) {
            allData = [...countyData];
            setTractData(allData);
            setLoadingProgress(90);
            const metrics = await calculateAndSetMetrics(allData, currentConfig, californiaDataForAverage);
            if (metrics && nextMapCenter && nextMapZoom) {
              mapCacheSet(loadKey, {
                tractData: allData,
                mapCenter: nextMapCenter,
                mapZoom: nextMapZoom,
                metrics
              });
            }
          } else {
            setError(`No data found for ${normalizedCounty} County. Please check the browser console for details.`);
          }
        } catch (loadError) {
          console.error('Error loading county data:', loadError);
          setError(`Failed to load ${normalizedCounty} County data: ${loadError instanceof Error ? loadError.message : 'Unknown error'}`);
        }
      } else {
        setError(`Could not determine county for location: ${location}`);
        setLoading(false);
        return;
      }

      setLoadingStage('Complete!');
      setLoadingProgress(100);

    } catch (err) {
      console.error('Error in data loading:', err);
      setError(`Failed to load data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentConfig?.id, location, dataScope]);

  // Load data for a specific county
  const loadDataForCounty = async (county: string, config: MapVariableConfig): Promise<any[]> => {
    try {
      if (config.dataSource === 'ev_by_tract_2023') {
        // Remove " County" suffix if present for matching
        const cleanCountyName = county.replace(/ County$/, '').toUpperCase();
        
        // Add timeout to the query
        const queryPromise = supabaseClient
          .from('ev_by_tract_2023')
          .select('*')
          .eq('County', cleanCountyName) // Use 'County' column (capitalized)
          .limit(5000);
        
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Query timeout after 20 seconds')), 20000)
        );
        
        const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any;
        
        if (error) {
          console.error('Error querying county data:', error);
          throw error;
        }
        return data || [];
      } else if (config.dataSource === 'cbei_tracts') {
        // For cbei_tracts, we need to match by county name in the 'name' field
        const { data, error } = await supabaseClient
          .from('cbei_tracts')
          .select('*')
          .eq('year', 2023) // Use year 2023 as specified
          .ilike('name', `%${county}%`)
          .limit(5000);
        
        if (error) throw error;
        return data || [];
      } else {
        // Get latest year
        const { data: yearRow, error: yearErr } = await supabaseClient
          .from(config.dataSource)
          .select('year')
          .order('year', { ascending: false })
          .limit(1);
        if (yearErr) throw yearErr;
        const latestYear = yearRow?.[0]?.year;

        const { data, error } = await supabaseClient
          .from(config.dataSource)
          .select('*')
          .eq('year', latestYear)
          .ilike('county', `%${county}%`)
          .limit(5000);
        
        if (error) throw error;
        return data || [];
      }
    } catch (err) {
      console.error('Error loading county data:', err);
      return [];
    }
  };

  // Load data for a specific region
  const loadDataForRegion = async (region: number, config: MapVariableConfig): Promise<any[]> => {
    try {
      // Get all counties in this region
      const { data: regionCounties, error: regionError } = await supabaseClient
        .from('regions')
        .select('county')
        .eq('region', region)
        .eq('geography_type', 'County'); // Use 'County' with capital C
      
      if (regionError) {
        console.error('Error fetching region counties:', regionError);
        throw regionError;
      }
      
      if (!regionCounties || regionCounties.length === 0) {
        return [];
      }

      const countyNames = regionCounties.map(r => r.county).filter(Boolean);
      
      if (countyNames.length === 0) {
        return [];
      }

      if (config.dataSource === 'ev_by_tract_2023') {
        // Clean county names for matching (remove " County" suffix and convert to uppercase)
        const cleanCountyNames = countyNames.map(name => name.replace(/ County$/, '').toUpperCase());
        
        const { data, error } = await supabaseClient
          .from('ev_by_tract_2023')
          .select('*')
          .in('County', cleanCountyNames) // Use 'County' column (capitalized)
          .limit(10000); // Increased limit for region data
        
        if (error) {
          console.error('Error querying region data:', error);
          throw error;
        }
        
        return data || [];
      } else if (config.dataSource === 'cbei_tracts') {
        // For cbei_tracts, match by county names in the 'name' field
        const { data, error } = await supabaseClient
          .from('cbei_tracts')
          .select('*')
          .eq('year', 2023) // Use year 2023 as specified
          .limit(10000);
        
        if (error) {
          console.error('Error querying cbei_tracts region data:', error);
          throw error;
        }
        
        // Filter by county names (since cbei_tracts doesn't have a direct county field)
        const filteredData = data?.filter(row => {
          const name = row.name?.toLowerCase() || '';
          return countyNames.some(county => name.includes(county.toLowerCase()));
        }) || [];
        
        return filteredData;
      } else {
        // Get latest year
        const { data: yearRow, error: yearErr } = await supabaseClient
          .from(config.dataSource)
          .select('year')
          .order('year', { ascending: false })
          .limit(1);
        if (yearErr) throw yearErr;
        const latestYear = yearRow?.[0]?.year;

        const { data, error } = await supabaseClient
          .from(config.dataSource)
          .select('*')
          .eq('year', latestYear)
          .in('county', countyNames)
          .limit(10000);
        
        if (error) throw error;
        return data || [];
      }
    } catch (err) {
      console.error('Error loading region data:', err);
      return [];
    }
  };

  // Load remaining California data
  const loadRemainingCaliforniaData = async (existingData: any[], config: MapVariableConfig): Promise<any[]> => {
    try {
      const existingTracts = new Set(existingData.map(d => d.tract));
      let allData: any[] = [];
      let offset = 0;
      const batchSize = 10000; // Large batch size to minimize requests
      
      if (config.dataSource === 'ev_by_tract_2023') {
        while (true) {
          let query = supabaseClient.from('ev_by_tract_2023').select('*');
          
          // Only exclude existing tracts if there are any
          if (existingTracts.size > 0) {
            query = query.not('tract', 'in', `(${Array.from(existingTracts).join(',')})`);
          }
          
          const { data, error } = await query.range(offset, offset + batchSize - 1);
          
          if (error) throw error;
          
          if (!data || data.length === 0) break;
          
          allData = allData.concat(data);
          offset += batchSize;
          
          // Safety check to prevent infinite loops
          if (data.length < batchSize) break;
        }
        
        return allData;
      } else if (config.dataSource === 'cbei_tracts') {
        while (true) {
          const { data, error } = await supabaseClient
            .from('cbei_tracts')
            .select('*')
            .eq('year', 2023) // Use year 2023 as specified
            .range(offset, offset + batchSize - 1);
          
          if (error) throw error;
          
          if (!data || data.length === 0) break;
          
          allData = allData.concat(data);
          offset += batchSize;
          
          // Safety check to prevent infinite loops
          if (data.length < batchSize) break;
        }
        
        return allData;
      } else {
        // Get latest year
        const { data: yearRow, error: yearErr } = await supabaseClient
          .from(config.dataSource)
          .select('year')
          .order('year', { ascending: false })
          .limit(1);
        if (yearErr) throw yearErr;
        const latestYear = yearRow?.[0]?.year;

        while (true) {
          const { data, error } = await supabaseClient
            .from(config.dataSource)
            .select('*')
            .eq('year', latestYear)
            .not('geofips', 'in', `(${Array.from(existingTracts).join(',')})`)
            .range(offset, offset + batchSize - 1);
          
          if (error) throw error;
          
          if (!data || data.length === 0) break;
          
          allData = allData.concat(data);
          offset += batchSize;
          
          // Safety check to prevent infinite loops
          if (data.length < batchSize) break;
        }
        
        return allData;
      }
    } catch (err) {
      console.error('Error loading remaining California data:', err);
      return [];
    }
  };

  // Calculate and set metrics
  const calculateAndSetMetrics = useCallback(async (data: any[], config: MapVariableConfig, californiaData?: any[]): Promise<MapMetrics | null> => {
    try {
      const variableId = config.id;
      const dataField = getDataFieldForVariable(variableId);
      const numericValues = data
        .map((t: any) => t?.[dataField])
        .filter((v: any) => v !== null && v !== undefined && !Number.isNaN(v))
        .map((v: any) => typeof v === 'string' ? parseFloat(String(v).replace(/[,]/g, '')) : v)
        .filter((v: any) => typeof v === 'number' && Number.isFinite(v));

      let currentValue = 0;
      let minValue: number | undefined;
      let maxValue: number | undefined;

      if (variableId === 'total_chargers') {
        currentValue = numericValues.reduce((a: number, b: number) => a + b, 0);
      } else if (variableId === 'zero_vehicle_households') {
        // Calculate zero vehicle household counts from percentage and population
        // Note: pct_zero_vehicle_households is stored as a decimal (0.085 = 8.5%), not as a percentage
        const avgHouseholdSize = 2.6;
        const countValues = data
          .map((t: any) => {
            const pctZeroVehicle = t.pct_zero_vehicle_households || 0;
            const population = t.total_population || 0;
            const households = population / avgHouseholdSize;
            return pctZeroVehicle * households; // pct is already a decimal, no need to divide by 100
          })
          .filter((v: any) => !Number.isNaN(v) && Number.isFinite(v));
        
        if (countValues.length > 0) {
          currentValue = countValues.reduce((a: number, b: number) => a + b, 0);
          minValue = Math.min(...countValues);
          maxValue = Math.max(...countValues);
        }
      } else if (variableId === 'carbon_footprint') {
        // Calculate carbon footprint from total emissions (data is already per-household)
        const carbonFootprintValues = data
          .map((t: any) => {
            const totalEmissions = (t.transportation_total_emissions || 0) + 
                                 (t.housing_total_emissions || 0) + 
                                 (t.food_total_emissions || 0) + 
                                 (t.goods_total_emissions || 0) + 
                                 (t.services_total_emissions || 0);
            return totalEmissions / 1000000; // Convert grams to metric tons
          })
          .filter((v: any) => !Number.isNaN(v) && Number.isFinite(v));
        
        if (carbonFootprintValues.length > 0) {
          currentValue = carbonFootprintValues.reduce((a: number, b: number) => a + b, 0) / carbonFootprintValues.length;
          minValue = Math.min(...carbonFootprintValues);
          maxValue = Math.max(...carbonFootprintValues);
        }
      } else if (variableId === 'owner_occupied_housing' || variableId === 'educational_attainment') {
        // Convert decimal to percentage (0.553 = 55.3%, 0.324 = 32.4%)
        const percentageValues = numericValues
          .map((v: number) => v * 100) // Convert decimal to percentage
          .filter((v: any) => !Number.isNaN(v) && Number.isFinite(v));
        
        if (percentageValues.length > 0) {
          currentValue = percentageValues.reduce((a: number, b: number) => a + b, 0) / percentageValues.length;
          minValue = Math.min(...percentageValues);
          maxValue = Math.max(...percentageValues);
        }
      } else if (variableId === 'income') {
        // For income, calculate the median of all tract medians (not the average)
        if (numericValues.length > 0) {
          const sortedValues = [...numericValues].sort((a, b) => a - b);
          const mid = Math.floor(sortedValues.length / 2);
          currentValue = sortedValues.length % 2 === 0
            ? (sortedValues[mid - 1] + sortedValues[mid]) / 2
            : sortedValues[mid];
        }
      } else if (variableId === 'political_lean') {
        // For political lean, calculate population-weighted average
        // dem_minus_rep is stored as decimal (e.g., 0.15 = 15% Democratic advantage)
        const weightedSum = data.reduce((sum: number, t: any) => {
          const leanValue = t?.[dataField];
          const population = t?.total_population || 0;
          if (leanValue !== null && leanValue !== undefined && !Number.isNaN(leanValue) && population > 0) {
            return sum + (leanValue * population);
          }
          return sum;
        }, 0);
        
        const totalPopulation = data.reduce((sum: number, t: any) => {
          const population = t?.total_population || 0;
          return sum + population;
        }, 0);
        
        if (totalPopulation > 0) {
          const rawDecimalValue = weightedSum / totalPopulation;
          currentValue = rawDecimalValue * 100;
        } else if (numericValues.length > 0) {
          // Fallback to simple average if no population data
          const rawAverage = numericValues.reduce((a: number, b: number) => a + b, 0) / numericValues.length;
          currentValue = rawAverage * 100;
        }
      } else if (numericValues.length > 0) {
        currentValue = numericValues.reduce((a: number, b: number) => a + b, 0) / numericValues.length;
      }

      // Reference values (statewide benchmarks)
      let californiaAverage = 0;
      let year = 2023;
      switch (variableId) {
        case 'income':
          californiaAverage = 95521;
          year = (data?.[0]?.year as number) || 2023;
          break;
        case 'ev_adoption':
          californiaAverage = 2.1;
          break;
        case 'total_chargers':
          californiaAverage = 50000;
          year = (data?.[0]?.year as number) || 2023;
          break;
        case 'vehicles_per_household':
          californiaAverage = 1.8;
          year = (data?.[0]?.year as number) || 2023;
          break;
        case 'population_density':
          californiaAverage = 250;
          year = (data?.[0]?.year as number) || 2023;
          break;
        case 'political_lean':
          year = (data?.[0]?.year as number) || 2023;
          
          // Calculate California average from all California data if available
          if (californiaData && californiaData.length > 0 && config.id === 'political_lean') {
            const californiaDataField = getDataFieldForVariable('political_lean');
            const californiaWeightedSum = californiaData.reduce((sum: number, t: any) => {
              const leanValue = t?.[californiaDataField];
              const population = t?.total_population || 0;
              if (leanValue !== null && leanValue !== undefined && !Number.isNaN(leanValue) && population > 0) {
                return sum + (leanValue * population);
              }
              return sum;
            }, 0);
            
            const californiaTotalPopulation = californiaData.reduce((sum: number, t: any) => {
              const population = t?.total_population || 0;
              return sum + population;
            }, 0);
            
            if (californiaTotalPopulation > 0) {
              const californiaAvgDecimal = californiaWeightedSum / californiaTotalPopulation;
              californiaAverage = californiaAvgDecimal * 100;
            } else {
              californiaAverage = 15; // Fallback (already as percentage)
            }
          } else {
            californiaAverage = 15; // Default fallback if no California data available
          }
          break;
        case 'carbon_footprint':
          californiaAverage = 45.2; // Converted from grams to metric tons
          year = (data?.[0]?.year as number) || 2023;
          break;
        case 'zero_vehicle_households':
          // California average: ~8.5% of households have no vehicles
          // Estimate: 39.5M population / 2.6 avg household size * 0.085 = ~1.29M zero vehicle households
          // But this is statewide, so for per-tract average, we'll use a reasonable estimate
          californiaAverage = 150; // Average zero vehicle households per tract (rough estimate)
          year = (data?.[0]?.year as number) || 2023;
          break;
        case 'owner_occupied_housing':
          // California average owner-occupied housing rate is approximately 55.3%
          // Data is stored as decimal (0.553 = 55.3%), but we'll display as percentage
          californiaAverage = 55.3; // Percentage
          year = (data?.[0]?.year as number) || 2023;
          break;
        case 'educational_attainment':
          // California average educational attainment (bachelor's degree or higher) is approximately 32.4%
          // Data is stored as decimal (0.324 = 32.4%), but we'll display as percentage
          californiaAverage = 32.4; // Percentage
          year = (data?.[0]?.year as number) || 2023;
          break;
        default:
          californiaAverage = 0;
          year = (data?.[0]?.year as number) || 2023;
      }

      const metrics: MapMetrics = {
        currentValue,
        californiaAverage,
        year,
        variable: variableId,
        ...(minValue !== undefined && { minValue }),
        ...(maxValue !== undefined && { maxValue }),
      };

      if (onDataLoaded) {
        onDataLoaded(metrics);
      }
      return metrics;
    } catch (metricsError) {
      console.error('Error calculating metrics:', metricsError);
    }
    return null;
  }, [onDataLoaded, getDataFieldForVariable]);

  // Reset map ready state when variable changes
  useEffect(() => {
    setMapReady(false);
  }, [selectedVariable]);

  // Load data based on selected variable and scope
  useEffect(() => {
    if (!currentConfig) {
      setError(`No configuration found for variable: ${selectedVariable}`);
      setLoading(false);
      return;
    }
    // Reset map ready state when loading new data
    setMapReady(false);
    loadDataByLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariable, currentConfig?.id, location, dataScope]);

  // Component to safely enable scroll wheel zoom after map is ready
  const EnableScrollWheelZoom: React.FC<{ enabled: boolean }> = ({ enabled }) => {
    const map = useMap();
    useEffect(() => {
      if (!enabled) {
        // Disable scroll wheel zoom if not ready
        try {
          if (map && map.scrollWheelZoom && map.scrollWheelZoom.enabled()) {
            map.scrollWheelZoom.disable();
          }
        } catch (err) {
          // Ignore errors when disabling
        }
        return;
      }
      
      // Add a longer delay and check if map container exists
      const timer = setTimeout(() => {
        try {
          if (!map || !map.getContainer) return;
          
          const container = map.getContainer();
          if (!container) return;
          
          // Check if container has the required Leaflet properties
          // Use type assertion to access Leaflet's internal property
          const leafletContainer = container as any;
          if (leafletContainer._leaflet_id === undefined) {
            // Retry after a bit more time
            setTimeout(() => {
              try {
                if (map.scrollWheelZoom && !map.scrollWheelZoom.enabled()) {
                  map.scrollWheelZoom.enable();
                }
              } catch (err) {
                // Scroll wheel zoom not available yet
              }
            }, 300);
            return;
          }
          
          // Only enable if map is fully initialized
          if (map.scrollWheelZoom && !map.scrollWheelZoom.enabled()) {
            map.scrollWheelZoom.enable();
          }
        } catch (err) {
          // Silently catch errors - map might not be ready yet
        }
      }, 800); // Longer delay to ensure map is fully ready
      
      return () => clearTimeout(timer);
    }, [enabled, map]);
    return null;
  };

  // Calculate deciles for the selected variable
  const calculateDecilesForVariable = useCallback((data: any[], variableId: string) => {
    if (!data || data.length === 0) return null;

    // Special handling for carbon footprint - calculate from total emissions directly
    if (variableId === 'carbon_footprint') {
      // Calculate carbon footprint from total emissions (data is already per-household)
      const carbonFootprintValues = data
        .map((t: any) => {
          const totalEmissions = (t.transportation_total_emissions || 0) + 
                               (t.housing_total_emissions || 0) + 
                               (t.food_total_emissions || 0) + 
                               (t.goods_total_emissions || 0) + 
                               (t.services_total_emissions || 0);
          return totalEmissions / 1000000; // Convert grams to metric tons
        })
        .filter((v: any) => !Number.isNaN(v) && Number.isFinite(v))
        .sort((a: number, b: number) => a - b);
      
      if (carbonFootprintValues.length === 0) return null;

      const deciles = [];
      for (let i = 1; i <= 9; i++) {
        const index = Math.floor((carbonFootprintValues.length * i) / 10);
        deciles.push(carbonFootprintValues[index]);
      }

      return {
        min: carbonFootprintValues[0],
        max: carbonFootprintValues[carbonFootprintValues.length - 1],
        breaks: deciles,
        count: carbonFootprintValues.length
      };
    }

    // For other variables, use the original logic
    const dataField = getDataFieldForVariable(variableId);
    let values = data
      .map((t: any) => t?.[dataField])
      .filter((v: any) => v !== null && v !== undefined && !Number.isNaN(v))
      .map((v: any) => typeof v === 'string' ? parseFloat(String(v).replace(/[,]/g, '')) : v)
      .filter((v: any) => typeof v === 'number' && Number.isFinite(v));
    
    // Convert owner-occupied housing from decimal to percentage before sorting
    if (variableId === 'owner_occupied_housing' || variableId === 'educational_attainment') {
      values = values.map((v: number) => v * 100);
    }
    
    values = values.sort((a: number, b: number) => a - b);

    if (values.length === 0) return null;

    // Special handling for zero_vehicle_households: use integer-based breaks
    if (variableId === 'zero_vehicle_households') {
      // Calculate zero vehicle household counts from percentage and population
      // California average household size is approximately 2.6 people per household
      // Note: pct_zero_vehicle_households is stored as a decimal (0.085 = 8.5%), not as a percentage
      const avgHouseholdSize = 2.6;
      const integerValues = data
        .map((t: any) => {
          const pctZeroVehicle = t.pct_zero_vehicle_households || 0;
          const population = t.total_population || 0;
          const households = population / avgHouseholdSize;
          return Math.round(pctZeroVehicle * households); // Round to integer
        })
        .filter((v: any) => !Number.isNaN(v) && Number.isFinite(v))
        .sort((a: number, b: number) => a - b);
      
      if (integerValues.length === 0) return null;

      const uniqueValues = Array.from(new Set(integerValues)).sort((a, b) => a - b);
      const maxValue = uniqueValues[uniqueValues.length - 1];
      
      // Create integer-based breaks
      const breaks: number[] = [];
      
      if (maxValue === 0) {
        breaks.push(0);
      } else if (maxValue <= 5) {
        // 0, 1, 2, 3, 4, 5
        breaks.push(0, 1, 2, 3, 4, 5);
      } else if (maxValue <= 10) {
        // 0, 1, 2, 3, 5, 10
        breaks.push(0, 1, 2, 3, 5, 10);
      } else if (maxValue <= 25) {
        // 0, 1, 2, 3, 5, 10, 15, 20, 25
        breaks.push(0, 1, 2, 3, 5, 10, 15, 20, 25);
      } else if (maxValue <= 50) {
        // 0, 1, 2, 3, 5, 10, 15, 25, 50
        breaks.push(0, 1, 2, 3, 5, 10, 15, 25, 50);
      } else if (maxValue <= 100) {
        // 0, 1, 2, 3, 5, 10, 20, 50, 100
        breaks.push(0, 1, 2, 3, 5, 10, 20, 50, 100);
      } else if (maxValue <= 200) {
        // 0, 1, 2, 5, 10, 25, 50, 100, 200
        breaks.push(0, 1, 2, 5, 10, 25, 50, 100, 200);
      } else {
        // For larger values, use a more spread out scale
        breaks.push(0, 1, 2, 5, 10, 25, 50, 100, 200);
        if (maxValue > 200) {
          breaks.push(maxValue);
        }
      }
      
      // Filter breaks to only include those <= maxValue and ensure we have at least 2
      const filteredBreaks = breaks.filter(b => b <= maxValue);
      if (filteredBreaks.length < 2) {
        filteredBreaks.push(maxValue);
      }
      
      // Ensure we have at most 9 breaks (for 10 color categories)
      const finalBreaks = filteredBreaks.slice(0, 9);
      
      return {
        min: integerValues[0],
        max: maxValue,
        breaks: finalBreaks,
        count: integerValues.length
      };
    }

    // Special handling for total_chargers: use integer-based breaks
    if (variableId === 'total_chargers') {
      // Round values to integers and get unique values
      const integerValues = values.map(v => Math.round(v));
      const uniqueValues = Array.from(new Set(integerValues)).sort((a, b) => a - b);
      const maxValue = uniqueValues[uniqueValues.length - 1];
      
      // Create integer-based breaks
      // Use a logarithmic-like scale for better distribution
      const breaks: number[] = [];
      
      if (maxValue === 0) {
        // All zeros - just return a single break
        breaks.push(0);
      } else if (maxValue <= 1) {
        // 0, 1
        breaks.push(0, 1);
      } else if (maxValue <= 3) {
        // 0, 1, 2, 3
        breaks.push(0, 1, 2, 3);
      } else if (maxValue <= 10) {
        // 0, 1, 2, 3, 5, 10
        breaks.push(0, 1, 2, 3, 5, 10);
      } else if (maxValue <= 25) {
        // 0, 1, 2, 3, 5, 10, 15, 20, 25
        breaks.push(0, 1, 2, 3, 5, 10, 15, 20, 25);
      } else if (maxValue <= 50) {
        // 0, 1, 2, 3, 5, 10, 15, 25, 50
        breaks.push(0, 1, 2, 3, 5, 10, 15, 25, 50);
      } else if (maxValue <= 100) {
        // 0, 1, 2, 3, 5, 10, 20, 50, 100
        breaks.push(0, 1, 2, 3, 5, 10, 20, 50, 100);
      } else if (maxValue <= 200) {
        // 0, 1, 2, 5, 10, 25, 50, 100, 200
        breaks.push(0, 1, 2, 5, 10, 25, 50, 100, 200);
      } else {
        // For larger values, use a more spread out scale
        // 0, 1, 2, 5, 10, 25, 50, 100, 200, maxValue
        breaks.push(0, 1, 2, 5, 10, 25, 50, 100, 200);
        if (maxValue > 200) {
          breaks.push(maxValue);
        }
      }
      
      // Filter breaks to only include those <= maxValue and ensure we have at least 2
      const filteredBreaks = breaks.filter(b => b <= maxValue);
      if (filteredBreaks.length < 2) {
        filteredBreaks.push(maxValue);
      }
      
      // Ensure we have at most 9 breaks (for 10 color categories)
      const finalBreaks = filteredBreaks.slice(0, 9);
      
      return {
        min: integerValues[0],
        max: maxValue,
        breaks: finalBreaks,
        count: integerValues.length
      };
    }

    // Special handling for owner-occupied housing: values already converted to percentages above
    if (variableId === 'owner_occupied_housing' || variableId === 'educational_attainment') {
      // Values are already converted to percentages in the extraction step above
      if (values.length === 0) return null;
      
      const deciles = [];
      for (let i = 1; i <= 9; i++) {
        const index = Math.floor((values.length * i) / 10);
        deciles.push(values[index]);
      }
      
      return {
        min: values[0],
        max: values[values.length - 1],
        breaks: deciles,
        count: values.length
      };
    }

    // Special handling for political lean: symmetric diverging breaks centered at 0
    if (variableId === 'political_lean') {
      // Convert decimal values to percentages (e.g., 0.15 -> 15)
      const percentageValues = values.map(v => v * 100);
      const maxAbs = percentageValues.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
      const M = maxAbs > 0 ? maxAbs : 1; // avoid zero-width
      const steps = 10; // 10 classes => 11 breaks
      const legendBreaks: number[] = [];
      for (let i = 0; i <= steps; i++) {
        const t = (i / steps) * 2 - 1; // -1 .. +1
        const val = t * M;
        legendBreaks.push(val);
      }
      // Ensure strictly increasing and include 0 midpoint exactly
      const cleaned = legendBreaks
        .slice()
        .sort((a, b) => a - b)
        .map((v) => Math.abs(v) < 1e-9 ? 0 : v);
      return { breaks: cleaned, min: cleaned[0], max: cleaned[cleaned.length - 1], count: values.length };
    }

    const deciles = [];
    for (let i = 1; i <= 9; i++) {
      const index = Math.floor((values.length * i) / 10);
      deciles.push(values[index]);
    }

    return {
      min: values[0],
      max: values[values.length - 1],
      breaks: deciles, // Changed from 'deciles' to 'breaks' to match MapLegend interface
      count: values.length
    };
  }, [getDataFieldForVariable]);

  // Use ALL California GeoJSON - no filtering by county
  const filteredGeoJson = useMemo(() => {
    if (!geoJsonData) return null;
    
    return geoJsonData;
  }, [geoJsonData]);

  // Memoize the stats calculation to prevent infinite loops
  const stats = useMemo(() => {
    if (!currentConfig || tractData.length === 0) return null;
    const result = calculateDecilesForVariable(tractData, currentConfig.id);
    return result;
  }, [currentConfig, tractData, calculateDecilesForVariable]);

  // Get style for each feature
  const getStyle = (feature: any) => {
    if (!currentConfig || !tractData.length) {
      return {
        fillColor: '#cccccc',
        weight: 0.5,
        opacity: 1,
        color: 'white',
        fillOpacity: 0.7
      };
    }

    const geoid = feature.properties.GEOID;
    // Convert both to numbers to handle leading zero differences
    const geoidNum = parseInt(geoid, 10);
    let tract;
    
    if (currentConfig.dataSource === 'cbei_tracts' || currentConfig.dataSource === 'acs5_income') {
      // For cbei_tracts and acs5_income, match by geofips column
      tract = tractData.find(t => {
        const geofipsValue = typeof t.geofips === 'string' ? parseInt(t.geofips, 10) : t.geofips;
        return geofipsValue === geoidNum;
      });
    } else {
      // For other tables (like ev_by_tract_2023), match by tract column
      // IMPORTANT: Match by full GEOID AND verify county code to avoid cross-county mismatches
      // GEOID format: State (2) + County (3) + Tract (6) = 11 digits
      // Extract county code from GEOID to verify correct county assignment
      const expectedCounty = getCountyFromGeoid(geoid);
      
      // Find matching tract, prioritizing the one with correct county assignment
      const matchingTracts = tractData.filter(t => {
        const tractValue = typeof t.tract === 'string' ? parseInt(t.tract, 10) : t.tract;
        return tractValue === geoidNum;
      });
      
      if (matchingTracts.length === 0) {
        tract = null;
      } else if (matchingTracts.length === 1) {
        tract = matchingTracts[0];
      } else {
        if (expectedCounty) {
          const correctCountyTract = matchingTracts.find(t => t.County === expectedCounty);
          if (correctCountyTract) {
            tract = correctCountyTract;
          } else {
            tract = matchingTracts[0];
          }
        } else {
          tract = matchingTracts[0];
        }
      }
    }
    
    if (!tract) {
      return {
        fillColor: '#cccccc',
        weight: 0.5,
        opacity: 1,
        color: 'white',
        fillOpacity: 0.3
      };
    }

    const dataField = getDataFieldForVariable(currentConfig.id);
    let value;
    
    // Special handling for carbon footprint - calculate from total emissions
    if (currentConfig.id === 'carbon_footprint') {
      const totalEmissions = tract.transportation_total_emissions + 
                            tract.housing_total_emissions + 
                            tract.food_total_emissions + 
                            tract.goods_total_emissions + 
                            tract.services_total_emissions;
      value = totalEmissions; // Data is already per-household, just use directly
    } else if (currentConfig.id === 'zero_vehicle_households') {
      // Calculate zero vehicle household count from percentage and population
      // California average household size is approximately 2.6 people per household
      // Note: pct_zero_vehicle_households is stored as a decimal (0.085 = 8.5%), not as a percentage
      const avgHouseholdSize = 2.6;
      const pctZeroVehicle = tract.pct_zero_vehicle_households || 0;
      const population = tract.total_population || 0;
      const households = population / avgHouseholdSize;
      value = pctZeroVehicle * households; // pct is already a decimal, no need to divide by 100
    } else {
      value = tract[dataField];
    }
    
    
    if (value === null || value === undefined || Number.isNaN(value)) {
      return {
        fillColor: '#cccccc',
        weight: 0.5,
        opacity: 1,
        color: 'white',
        fillOpacity: 0.3
      };
    }

    let numericValue = typeof value === 'string' ? parseFloat(String(value).replace(/[,]/g, '')) : value;
    
    if (!Number.isFinite(numericValue)) {
      return {
        fillColor: '#cccccc',
        weight: 0.5,
        opacity: 1,
        color: 'white',
        fillOpacity: 0.3
      };
    }

    // Convert decimal to percentage for political lean
    if (currentConfig.id === 'political_lean') {
      numericValue = numericValue * 100;
    }

    // Convert decimal to percentage for owner-occupied housing (0.553 = 55.3%)
    if (currentConfig.id === 'owner_occupied_housing' || currentConfig.id === 'educational_attainment') {
      numericValue = numericValue * 100;
    }

    // Convert grams to metric tons for carbon footprint (divide by 1,000,000)
    if (currentConfig.id === 'carbon_footprint') {
      numericValue = numericValue / 1000000;
    }

    if (!stats) {
      return {
        fillColor: '#cccccc',
        weight: 0.5,
        opacity: 1,
        color: 'white',
        fillOpacity: 0.3
      };
    }

    const color = currentConfig.colorFunction(numericValue, stats.breaks);
    
    return {
      fillColor: color,
      weight: 0.5,
      opacity: 1,
      color: 'white',
      fillOpacity: 0.7
    };
  };

  // Handle feature click
  const onEachFeature = (feature: any, layer: any) => {
    const geoid = feature.properties.GEOID;
    // Convert both to numbers to handle leading zero differences
    const geoidNum = parseInt(geoid, 10);
    let tract: any;
    
    if (currentConfig?.dataSource === 'cbei_tracts' || currentConfig?.dataSource === 'acs5_income') {
      // For cbei_tracts and acs5_income, match by geofips column
      tract = tractData.find(t => {
        const geofipsValue = typeof t.geofips === 'string' ? parseInt(t.geofips, 10) : t.geofips;
        return geofipsValue === geoidNum;
      });
    } else {
      // For other tables (like ev_by_tract_2023), match by tract column with county verification
      // Extract county code from GEOID to verify correct county assignment
      const expectedCounty = getCountyFromGeoid(geoid);
      
      const matchingTracts = tractData.filter(t => {
        const tractValue = typeof t.tract === 'string' ? parseInt(t.tract, 10) : t.tract;
        return tractValue === geoidNum;
      });
      
      if (matchingTracts.length === 0) {
        tract = null;
      } else if (matchingTracts.length === 1) {
        tract = matchingTracts[0];
      } else {
        if (expectedCounty) {
          const correctCountyTract = matchingTracts.find(t => t.County === expectedCounty);
          if (correctCountyTract) {
            tract = correctCountyTract;
          } else {
            tract = matchingTracts[0];
          }
        } else {
          tract = matchingTracts[0];
        }
      }
    }
    
    if (tract && currentConfig) {
      const dataField = getDataFieldForVariable(currentConfig.id);
      let value = tract[dataField];
      
      // Special handling for zero_vehicle_households: calculate count from percentage
      // Note: pct_zero_vehicle_households is stored as a decimal (0.085 = 8.5%), not as a percentage
      if (currentConfig.id === 'zero_vehicle_households') {
        const avgHouseholdSize = 2.6;
        const pctZeroVehicle = tract.pct_zero_vehicle_households || 0;
        const population = tract.total_population || 0;
        const households = population / avgHouseholdSize;
        value = pctZeroVehicle * households; // pct is already a decimal, no need to divide by 100
      }
      
      // Convert decimal to percentage for owner-occupied housing and educational attainment display
      if ((currentConfig.id === 'owner_occupied_housing' || currentConfig.id === 'educational_attainment') && value !== null && value !== undefined) {
        value = typeof value === 'number' ? value * 100 : parseFloat(value) * 100;
      }
      
      // Helper function to format political lean as "D +15" or "R +2"
      const formatPoliticalLean = (leanValue: number): string => {
        if (leanValue > 0) {
          return `D +${leanValue.toFixed(1)}`;
        } else if (leanValue < 0) {
          return `R +${Math.abs(leanValue).toFixed(1)}`;
        } else {
          return 'Even';
        }
      };
      
      const displayValue = value !== null && value !== undefined ? 
        (typeof value === 'number' ? value.toLocaleString() : value) : 'No data';
      
      // Special popup for carbon footprint with category breakdown
      if (currentConfig.id === 'carbon_footprint') {
        // Helper function to convert grams to metric tons (data is already per-household)
        const convertToMetricTons = (value: any) => {
          if (value === null || value === undefined) return 'No data';
          const numericValue = typeof value === 'string' ? parseFloat(value) : value;
          if (isNaN(numericValue)) return 'No data';
          return (numericValue / 1000000).toFixed(1);
        };

        layer.bindPopup(`
          <div>
            <strong>Census Tract ${geoid}</strong><br/>
            <strong>Total Carbon Footprint:</strong> ${convertToMetricTons(tract.transportation_total_emissions + tract.housing_total_emissions + tract.food_total_emissions + tract.goods_total_emissions + tract.services_total_emissions)} MT CO₂e/HH<br/>
            <strong>Transportation:</strong> ${convertToMetricTons(tract.transportation_total_emissions)} MT CO₂e/HH<br/>
            <strong>Housing:</strong> ${convertToMetricTons(tract.housing_total_emissions)} MT CO₂e/HH<br/>
            <strong>Food:</strong> ${convertToMetricTons(tract.food_total_emissions)} MT CO₂e/HH<br/>
            <strong>Goods:</strong> ${convertToMetricTons(tract.goods_total_emissions)} MT CO₂e/HH<br/>
            <strong>Services:</strong> ${convertToMetricTons(tract.services_total_emissions)} MT CO₂e/HH<br/>
            <strong>Location:</strong> ${tract.name || 'Unknown'}
          </div>
        `);
      } else if (currentConfig.id === 'political_lean') {
        // Special formatting for political lean: "D +15" or "R +2"
        // Convert from decimal to percentage (e.g., 0.15 -> 15) to match style function
        let numericValue = typeof value === 'number' ? value : parseFloat(value);
        if (!isNaN(numericValue)) {
          numericValue = numericValue * 100; // Convert decimal to percentage
        }
        const formattedValue = !isNaN(numericValue) ? formatPoliticalLean(numericValue) : 'No data';
        
        layer.bindPopup(`
          <div>
            <strong>Census Tract ${geoid}</strong><br/>
            <strong>${currentConfig.legendConfig?.title}:</strong> ${formattedValue}<br/>
            <strong>County:</strong> ${tract.County || 'Unknown'}
          </div>
        `);
      } else {
        // Format value with unit for display
        let formattedValue = displayValue;
        if (value !== null && value !== undefined && displayValue !== 'No data') {
          const unit = currentConfig.legendConfig?.unit || '';
          if (unit === ' min') {
            formattedValue = `${parseFloat(value.toString()).toFixed(1)}${unit}`;
          } else if (unit === '%') {
            formattedValue = `${parseFloat(value.toString()).toFixed(1)}${unit}`;
          } else if (unit === ' MT CO₂e/HH') {
            formattedValue = `${parseFloat(value.toString()).toFixed(1)}${unit}`;
          } else {
            formattedValue = typeof value === 'number' ? value.toLocaleString() + (unit ? ` ${unit}` : '') : value;
          }
        }
        
        layer.bindPopup(`
          <div>
            <strong>Census Tract ${geoid}</strong><br/>
            <strong>${currentConfig.legendConfig?.title}:</strong> ${formattedValue}<br/>
            <strong>County:</strong> ${tract.County || 'Unknown'}
          </div>
        `);
      }
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px',
        padding: '20px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            width: '200px', 
            height: '4px', 
            backgroundColor: '#e0e0e0', 
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${loadingProgress}%`,
              height: '100%',
              backgroundColor: '#007bff',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
          {loadingStage}
        </div>
        <div style={{ fontSize: '14px', color: '#666' }}>
          {loadingProgress}% complete
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px',
        color: '#dc3545'
      }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
            Error loading map data
          </div>
          <div style={{ fontSize: '14px' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!filteredGeoJson) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px'
      }}>
        <div>Loading map data...</div>
      </div>
    );
  }

  // During active loading, show a transient "calculating" state while stats are computed
  // Once loading is complete, fall through and render the map even if stats are null
  if (loading && !stats && tractData.length > 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px'
      }}>
        <div>Calculating map statistics...</div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <div style={{ 
        marginBottom: '10px', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '20px',
        flexWrap: 'wrap'
      }}>
        {!hideVariableSelector && !lockedVariable && (
          <>
            <MapVariableSelector
              selectedVariable={selectedVariable}
              onVariableChange={setSelectedVariable}
              allowedVariables={allowedVariables}
            />
            {enableScopeSelector && (
              <DataLoadingScopeSelector
                selectedScope={dataScope}
                onScopeChange={setDataScope}
              />
            )}
          </>
        )}
      </div>
      
      <div ref={mapContainerRef} style={{ position: 'relative', height: '500px' }}>
        <MapContainer
          key={`map-${selectedVariable}-${location}-${dataScope}`}
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
          zoomControl={true}
          doubleClickZoom={true}
          dragging={true}
          whenReady={() => {
            setTimeout(() => {
              if (!isMountedRef.current) return;
              try {
                const mapElement = document.querySelector('.leaflet-container');
                if (mapElement && isMountedRef.current) {
                  setMapReady(true);
                } else {
                  setTimeout(() => {
                    if (isMountedRef.current) setMapReady(true);
                  }, 300);
                }
              } catch (err) {
                if (isMountedRef.current) setTimeout(() => setMapReady(true), 500);
              }
            }, 500);
          }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {mapReady && <EnableScrollWheelZoom enabled={mapReady} />}
          {mapReady && filteredGeoJson && stats && (
            <GeoJSON
              key={`geojson-${selectedVariable}-${tractData.length}`}
              data={filteredGeoJson}
              style={getStyle}
              onEachFeature={onEachFeature}
            />
          )}
        </MapContainer>
        
        {currentConfig && tractData.length > 0 && stats && (
          <div className="map-legend-wrapper">
            <MapLegend
              title={currentConfig.legendConfig?.title || 'Data'}
              dataYear={currentConfig.legendConfig?.dataYear || '2023'}
              colorScale={currentConfig.legendConfig?.colorScale || ['#ffffcc', '#ff4444']}
              breaks={stats?.breaks || []}
              unit={currentConfig.legendConfig?.unit || ''}
              tractCount={tractData.length}
              referenceValue={currentConfig.legendConfig?.referenceValue || undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericMapTiered;
